import {
  collection,
  CollectionReference,
  doc,
  DocumentReference,
  Firestore,
  query,
  where,
} from 'firebase/firestore';
import isEqual from 'lodash/isEqual';
import { collectionData, docData } from 'rxfire/firestore';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { ICrossTestMetricSelection, IUser } from '../../../types';
import { authenticatedUserProvider } from '../../userProvider';
import { IDateRange } from '../dates/dateRangeProvider';
import { ISettingsTemplate } from './types';

export interface ITrendReportSettings {
  metrics: ICrossTestMetricSelection[];
  whiskers: boolean;
}

interface ITrendReportSettingEntity {
  selected: ISettingsTemplate<ITrendReportSettings>;
  timeframe: IDateRange;
  whiskers: boolean;
}

export interface ITrendReportSettingsState {
  selected: ISettingsTemplate<ITrendReportSettings>;
  templates: Array<ISettingsTemplate<ITrendReportSettings>>;
  timeframe: IDateRange;
  whiskers: boolean;
}

/**
 * @description observes dashboard settings
 */

export const trendReportSettingsProvider =
  (): Observable<ITrendReportSettingsState> => {
    return authenticatedUserProvider().pipe(
      switchMap((user: IUser): Observable<ITrendReportSettingsState> => {
        return combineLatest([
          selectedTemplateProvider({
            userId: user.id,
            firestore: user.services.firestore,
          }),
          trendTimeframeProvider({
            userId: user.id,
            firestore: user.services.firestore,
          }),
          templatesProvider({
            userId: user.id,
            firestore: user.services.firestore,
          }),
          whiskerStatusProvider({
            userId: user.id,
            firestore: user.services.firestore,
          }),
        ]).pipe(
          map(
            (
              result: [
                ISettingsTemplate<ITrendReportSettings>,
                IDateRange,
                Array<ISettingsTemplate<ITrendReportSettings>>,
                boolean
              ]
            ): ITrendReportSettingsState => {
              const [selected, timeframe, templates, whiskers] = result;
              if (selected?.id) {
                return {
                  selected,
                  timeframe,
                  templates,
                  whiskers,
                };
              } else {
                return {
                  selected: null,
                  timeframe,
                  templates,
                  whiskers,
                };
              }
            }
          )
        );
      })
    );
  };

interface ISelectedTemplateProviderArgs {
  userId: string;
  firestore: Firestore;
}

/**
 * @description observes the selected template
 * @param userId
 * @returns
 */
const selectedTemplateProvider = (
  args: ISelectedTemplateProviderArgs
): Observable<ISettingsTemplate<ITrendReportSettings>> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/trendReport`
    ) as DocumentReference<ITrendReportSettingEntity>
  ).pipe(
    map(
      (
        entity: ITrendReportSettingEntity
      ): ISettingsTemplate<ITrendReportSettings> => entity?.selected
    ),
    distinctUntilChanged(
      (
        prev: ISettingsTemplate<ITrendReportSettings>,
        curr: ISettingsTemplate<ITrendReportSettings>
      ): boolean => isEqual(prev, curr)
    )
  );
};

interface ITrendTimeframeArgs {
  userId: string;
  firestore: Firestore;
}

/**
 * @description observes the timeframe
 * @param userId
 * @returns
 */
const trendTimeframeProvider = (
  args: ITrendTimeframeArgs
): Observable<IDateRange> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/trendReport`
    ) as DocumentReference<ITrendReportSettingEntity>
  ).pipe(
    map((entity: ITrendReportSettingEntity): IDateRange => entity?.timeframe),
    distinctUntilChanged((prev: IDateRange, curr: IDateRange): boolean =>
      isEqual(prev, curr)
    )
  );
};

interface IWhiskerArgs {
  userId: string;
  firestore: Firestore;
}

/**
 * @description observes the whiskers
 * @param userId
 * @returns
 */
const whiskerStatusProvider = (args: IWhiskerArgs): Observable<boolean> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/trendReport`
    ) as DocumentReference<ITrendReportSettingEntity>
  ).pipe(
    map((entity: ITrendReportSettingEntity): boolean => entity?.whiskers),
    distinctUntilChanged((prev: boolean, curr: boolean): boolean =>
      isEqual(prev, curr)
    )
  );
};

interface ITemplateProviderArgs {
  userId: string;
  firestore: Firestore;
}

/**
 * @description observes the settings templates the user has saved
 * @param userId
 * @returns
 */
const templatesProvider = (
  args: ITemplateProviderArgs
): Observable<Array<ISettingsTemplate<ITrendReportSettings>>> => {
  const { userId, firestore } = args;
  return collectionData(
    query(
      collection(
        firestore,
        `userProfiles/${userId}/settings/trendReport/templates`
      ) as CollectionReference<ISettingsTemplate<ITrendReportSettings>>,
      where('active', '==', true)
    )
  );
};
